import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { fadeInOut } from '../shared/animations';
import { LanguageService } from '../services/language.service';
import { ReelService } from '../services/reel.service';
import { CatalogService } from '../services/catalog.service';
import { AnalyticsService } from '../services/analytics.service';
import { CatalogItem, ReelBanner, resolveReelText } from '../models/reel.model';

// --- Timing / geometry constants (verbatim from the iOS ReelViewModel parity spec) ---
const TICK_MS = 50;
const HOLD_THRESHOLD_MS = 180;
const UP_NEXT_LEAD_MS = 1500;
const COACH_AUTO_DISMISS_MS = 2800;
const MAX_RECOVERY = 3;
const RETRY_BACKOFF_MS = 500;
const TAP_PREV_MAX = 0.35;
const TAP_NEXT_MIN = 0.65;
const COACH_KEY = 'reelCoachShown';

/**
 * The full-screen Feather Reel player. Ported 1:1 from iOS `ReelView` + `ReelViewModel`.
 *
 * OnPush: the ~50ms progress tick and video events run outside Angular and drive `detectChanges()`
 * on this subtree only, so 20Hz updates never thrash the app's global change detection. The `<video>`
 * element stays mounted across clip/screen changes so its `@ViewChild` ref is stable and controllable.
 */
@Component({
    selector: 'app-reel-overlay',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './reel-overlay.component.html',
    styleUrls: ['./reel-overlay.component.scss'],
    animations: [fadeInOut],
    host: {
        tabindex: '0',
        role: 'dialog',
        'aria-modal': 'true',
    },
    standalone: false
})
export class ReelOverlayComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() banner!: ReelBanner;

  // Two-element video pool: the active slot plays, the other prefetches the next clip so advances
  // are seamless. `activeSlot` is read by the template to show/hide the right element.
  @ViewChild('slot0') slot0Ref?: ElementRef<HTMLVideoElement>;
  @ViewChild('slot1') slot1Ref?: ElementRef<HTMLVideoElement>;
  activeSlot: 0 | 1 = 0;
  private slotClipIndex: [number, number] = [-1, -1];

  screen: 'reel' | 'end' = 'reel';
  currentIndex = 0;
  paused = false;
  progress = 0;
  showCoach = false;
  showPoster = false;
  isBuffering = false;
  catalog: CatalogItem[] = [];

  private imageElapsedMs = 0;
  private recoveryAttempts = 0;
  /** One `reel_reward_click` per reel open. @see onAskPanelTap for why repeats are not counted. */
  private didLogRewardTap = false;
  private readonly failedVideoClips = new Set<number>();
  private tickHandle: any = null;
  private coachTimer: any = null;
  private retryTimer: any = null;
  private holdTimer: any = null;
  private didHold = false;
  private pointerActive = false;
  private catalogRequestedFor: string | null = null;

  constructor(
    private reelService: ReelService,
    private catalogService: CatalogService,
    private language: LanguageService,
    private translate: TranslateService,
    private analytics: AnalyticsService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private host: ElementRef<HTMLElement>
  ) {}

  // ---- lifecycle ----

  ngOnInit(): void {
    if (!this.banner || this.banner.clips.length === 0) {
      this.reelService.close();
      return;
    }
    this.showCoach = !this.coachShownBefore() && this.clips.length > 1;
    this.analytics.log('reel_open', {
      banner_id: this.banner.bannerId,
      media_type: this.mediaTypeAt(this.currentIndex)
    });
    // The first clip never goes through goTo(), so log its view here — otherwise every reel would
    // look like it started at clip 2 and the drop-off curve would lose its baseline.
    this.logCurrentClipView();
    // Prefetch the end-card catalog now (during story playback) so the marquee is ready before the
    // end card renders — otherwise it pops in late and shifts the flex hero image upward.
    this.loadCatalog();
  }

  ngAfterViewInit(): void {
    // Defer to a microtask so we mutate state after the current CD pass (avoids
    // ExpressionChangedAfterItHasBeenChecked) but still inside Angular's zone.
    Promise.resolve().then(() => {
      if (!this.banner || this.banner.clips.length === 0) return;
      this.applyCurrentClip();
      this.startTick();
      if (this.showCoach) this.scheduleCoachDismiss();
      this.host?.nativeElement?.focus({ preventScroll: true });
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.clearTick();
    this.clearTimers();
    this.pauseAllVideos();
  }

  // ---- derived state ----

  get clips() {
    return this.banner.clips;
  }
  get total(): number {
    return this.clips.length;
  }
  get currentClip() {
    return this.clips[this.currentIndex];
  }
  get isSingleClip(): boolean {
    return this.total <= 1;
  }
  get isLastClip(): boolean {
    return this.currentIndex >= this.total - 1;
  }
  /** A video clip that has NOT exhausted its recovery attempts. Failed videos behave as image stills. */
  get isVideoClip(): boolean {
    const c = this.currentClip;
    return !!c && c.isVideo && !this.failedVideoClips.has(this.currentIndex);
  }

  get stillUrl(): string {
    const c = this.currentClip;
    if (!c) return '';
    // Video clips (playable or failed) use the poster; image clips use the image itself.
    return c.isVideo ? c.thumbnailUrl ?? '' : c.mediaUrl;
  }
  get showStill(): boolean {
    // Cover a video with its poster until it plays; an image / failed video IS the still.
    return this.isVideoClip ? this.showPoster : true;
  }
  get stillObjectFit(): string {
    if (this.isVideoClip) return 'cover';
    return this.currentClip?.isPortrait ? 'cover' : 'contain';
  }

  // ---- localized text (resolved per current language at render) ----

  private get lang(): string {
    return this.language.getCurrentLang();
  }
  get captionTitle(): string {
    return resolveReelText(this.currentClip?.title, this.lang, '');
  }
  get captionSubtitle(): string {
    return resolveReelText(this.currentClip?.description, this.lang, '');
  }
  get upNextTeaser(): string {
    return resolveReelText(this.currentClip?.teaser, this.lang, '');
  }
  get panelTitle(): string {
    return resolveReelText(this.banner.rewardName, this.lang, this.translate.instant('reel.panelTitle'));
  }
  get panelSubtitle(): string {
    return resolveReelText(
      this.banner.rewardInstructions,
      this.lang,
      this.translate.instant('reel.panelSubtitle')
    );
  }
  get endTitle(): string {
    return resolveReelText(this.banner.detailTitle, this.lang, '');
  }
  get endDescription(): string {
    return resolveReelText(this.banner.detailDescription, this.lang, '');
  }
  get watchAgainLabel(): string {
    return resolveReelText(this.banner.actionButtonText, this.lang, this.translate.instant('reel.watchAgain'));
  }
  get closeLabel(): string {
    return resolveReelText(this.banner.secondaryButtonText, this.lang, this.translate.instant('reel.close'));
  }

  get showUpNext(): boolean {
    if (this.screen !== 'reel' || this.isLastClip || this.total === 0) return false;
    const totalSec = this.currentClipTotalSeconds();
    const remainingMs = totalSec * (1 - this.progress) * 1000;
    return remainingMs > 0 && remainingMs <= UP_NEXT_LEAD_MS;
  }

  segmentFill(index: number): number {
    if (index < this.currentIndex) return 1;
    if (index > this.currentIndex) return 0;
    return Math.min(Math.max(this.progress, 0), 1);
  }

  trackByIndex(index: number): number {
    return index;
  }

  // ---- tick / advance ----

  private startTick(): void {
    this.clearTick();
    this.ngZone.runOutsideAngular(() => {
      this.tickHandle = setInterval(() => this.tick(), TICK_MS);
    });
  }

  private tick(): void {
    if (this.screen !== 'reel' || this.paused) return;
    const c = this.currentClip;
    if (!c) return;

    if (this.isVideoClip) {
      const v = this.activeVideo();
      if (v && v.duration && isFinite(v.duration) && v.duration > 0) {
        this.progress = Math.min(Math.max(v.currentTime / v.duration, 0), 1);
      }
      // Videos advance via the 'ended' event, not the tick.
    } else {
      this.imageElapsedMs += TICK_MS;
      const durationMs = Math.max(c.durationSeconds, 0.001) * 1000;
      this.progress = Math.min(this.imageElapsedMs / durationMs, 1);
      if (this.imageElapsedMs >= durationMs) {
        this.ngZone.run(() => this.next());
        return;
      }
    }
    this.cdr.detectChanges();
  }

  next(): void {
    if (this.isLastClip) {
      this.goToEnd();
      return;
    }
    this.goTo(this.currentIndex + 1);
  }

  prev(): void {
    if (this.currentIndex <= 0) {
      this.restartCurrent();
      return;
    }
    this.goTo(this.currentIndex - 1);
  }

  jumpTo(index: number): void {
    this.goTo(index);
  }

  /**
   * A tap on the persistent ask panel below the video.
   *
   * The panel does NOTHING when tapped, deliberately — this is a demand probe for whether it is
   * worth building a real interaction there, and the number decides. Nothing visible may change as
   * a result: no cursor, no highlight, no navigation.
   *
   * ONCE PER REEL OPEN. Because the control is inert a guest who taps and gets no response taps
   * again, so counting every tap would measure frustration rather than demand and push
   * clicks/reel_open above 100%. Not reset by watchAgain(), which mirrors reel_open — the two have
   * to stay one-to-one or the rate loses its denominator.
   */
  onAskPanelTap(): void {
    if (this.didLogRewardTap) {
      return;
    }
    this.didLogRewardTap = true;
    this.analytics.log('reel_reward_click', { banner_id: this.banner.bannerId });
  }

  watchAgain(): void {
    this.analytics.log('reel_action_click', { banner_id: this.banner.bannerId, action_type: 'watch_again' });
    this.goTo(0);
  }

  close(): void {
    // Only the END-card close is a conversion signal. Back/Escape also close the reel, and counting
    // those would inflate the action rate with non-actions.
    if (this.screen === 'end') {
      this.analytics.log('reel_action_click', { banner_id: this.banner.bannerId, action_type: 'close' });
    }
    this.reelService.close();
  }

  private goTo(index: number): void {
    this.screen = 'reel';
    this.currentIndex = Math.min(Math.max(index, 0), this.total - 1);
    this.applyCurrentClip();
    this.logCurrentClipView();
    this.cdr.markForCheck();
  }

  /** One per clip reached: open + complete alone give a completion rate but no drop-off curve. */
  private logCurrentClipView(): void {
    this.analytics.log('reel_item_view', {
      banner_id: this.banner.bannerId,
      content_id: String(this.currentIndex),
      media_type: this.mediaTypeAt(this.currentIndex)
    });
  }

  /** A video that failed and now shows its poster counts as an image — that is what was seen. */
  private mediaTypeAt(index: number): string {
    const clip = this.clips[index];
    return clip?.isVideo && !this.failedVideoClips.has(index) ? 'video' : 'image';
  }

  private restartCurrent(): void {
    this.applyCurrentClip();
    this.cdr.markForCheck();
  }

  private goToEnd(): void {
    this.screen = 'end';
    this.showCoach = false;
    this.paused = false;
    this.pauseAllVideos();
    this.loadCatalog();
    // The catalog id rides along ONLY when the marquee actually has cards to draw. CatalogService
    // resolves a missing or broken `app-catalogs/{id}` doc to an empty list silently, so without
    // this a catalog arriving empty is indistinguishable from one never configured.
    this.analytics.log('reel_complete', {
      banner_id: this.banner.bannerId,
      content_id: this.catalog.length > 0 ? this.banner.endCardCatalogId : undefined
    });
    this.cdr.markForCheck();
  }

  private applyCurrentClip(): void {
    this.progress = 0;
    this.imageElapsedMs = 0;
    this.recoveryAttempts = 0;
    this.clearRetry();

    const c = this.currentClip;
    if (!c) return;

    if (this.isVideoClip) {
      this.showPoster = true;
      this.isBuffering = true;
      this.activateVideoClip();
    } else {
      this.showPoster = false;
      this.isBuffering = false;
      this.pauseAllVideos();
      this.prefetchNext();
    }
  }

  // ---- video control (double-buffered: active slot plays, the other prefetches the next clip) ----

  private slotEl(slot: 0 | 1): HTMLVideoElement | undefined {
    return (slot === 0 ? this.slot0Ref : this.slot1Ref)?.nativeElement;
  }

  private activeVideo(): HTMLVideoElement | undefined {
    return this.slotEl(this.activeSlot);
  }

  /** Show the current video clip — reuse a prefetched slot for a seamless swap, else load it fresh. */
  private activateVideoClip(): void {
    const idx = this.currentIndex;
    const c = this.currentClip;
    if (!c) return;

    const other = (this.activeSlot ^ 1) as 0 | 1;
    const otherEl = this.slotEl(other);
    if (this.slotClipIndex[other] === idx && otherEl && !otherEl.error) {
      // The clip was prefetched into the other slot — swap and play (already buffered → instant).
      this.activeSlot = other;
      this.playActive();
    } else {
      this.loadSlot(this.activeSlot, idx, c.mediaUrl, true);
    }
    this.prefetchNext();
  }

  /** Buffer the next video clip into the inactive slot, and warm the next poster. */
  private prefetchNext(): void {
    const nextIdx = this.currentIndex + 1;
    if (nextIdx >= this.total) return;
    const next = this.clips[nextIdx];

    if (next.isVideo && !this.failedVideoClips.has(nextIdx) && next.mediaUrl) {
      const inactive = (this.activeSlot ^ 1) as 0 | 1;
      if (this.slotClipIndex[inactive] !== nextIdx) {
        this.loadSlot(inactive, nextIdx, next.mediaUrl, false);
      }
    }
    if (next.thumbnailUrl) {
      const img = new Image();
      img.src = next.thumbnailUrl;
    }
  }

  private loadSlot(slot: 0 | 1, clipIndex: number, url: string, play: boolean): void {
    const v = this.slotEl(slot);
    if (!v) return;
    this.slotClipIndex[slot] = clipIndex;
    try {
      v.src = url;
      v.muted = true;
      v.currentTime = 0;
      v.preload = 'auto';
      v.load();
      if (play) this.playElement(v);
    } catch {
      if (play) this.onVideoError(slot);
    }
  }

  private playActive(): void {
    const v = this.activeVideo();
    if (v) this.playElement(v);
  }

  private playElement(v: HTMLVideoElement): void {
    const p = v.play();
    if (p && typeof p.then === 'function') {
      p.catch(() => {
        // Autoplay blocked (rare — the reel opens from a user tap). Keep the poster; a tap advances.
        this.isBuffering = false;
        this.cdr.markForCheck();
      });
    }
  }

  private pauseAllVideos(): void {
    for (const slot of [0, 1] as const) {
      const v = this.slotEl(slot);
      if (v) {
        try {
          v.pause();
        } catch {
          /* ignore */
        }
      }
    }
  }

  onVideoPlaying(slot: 0 | 1): void {
    if (slot !== this.activeSlot) return;
    this.showPoster = false;
    this.isBuffering = false;
    this.recoveryAttempts = 0;
    this.cdr.markForCheck();
  }

  onVideoWaiting(slot: 0 | 1): void {
    if (slot !== this.activeSlot) return;
    this.isBuffering = true;
    this.cdr.markForCheck();
  }

  onVideoEnded(slot: 0 | 1): void {
    if (slot !== this.activeSlot) return;
    if (this.screen === 'reel' && this.isVideoClip) this.next();
  }

  onVideoError(slot: 0 | 1): void {
    if (slot !== this.activeSlot) return; // a prefetch-slot error re-surfaces if/when it becomes active
    if (this.isVideoClip && this.recoveryAttempts < MAX_RECOVERY) {
      this.recoveryAttempts++;
      this.clearRetry();
      this.ngZone.runOutsideAngular(() => {
        this.retryTimer = setTimeout(() => this.ngZone.run(() => this.reprepareVideo()), RETRY_BACKOFF_MS);
      });
    } else {
      this.markCurrentVideoUnsupported();
    }
  }

  private reprepareVideo(): void {
    const c = this.currentClip;
    if (!c || !this.isVideoClip) return;
    this.showPoster = true;
    this.isBuffering = true;
    this.loadSlot(this.activeSlot, this.currentIndex, c.mediaUrl, true);
    this.cdr.markForCheck();
  }

  /** After 3 failed attempts: keep the poster and let the tick advance it like an image (never skip). */
  private markCurrentVideoUnsupported(): void {
    this.failedVideoClips.add(this.currentIndex);
    this.pauseAllVideos();
    this.showPoster = true;
    this.isBuffering = false;
    this.imageElapsedMs = 0;
    this.progress = 0;
    this.cdr.markForCheck();
  }

  // ---- gestures ----

  onPointerDown(): void {
    if (this.screen !== 'reel') return;
    this.pointerActive = true;
    this.didHold = false;
    this.clearHold();
    this.ngZone.runOutsideAngular(() => {
      this.holdTimer = setTimeout(() => {
        this.ngZone.run(() => {
          this.didHold = true;
          this.setPaused(true);
        });
      }, HOLD_THRESHOLD_MS);
    });
  }

  onPointerUp(ev: PointerEvent): void {
    if (!this.pointerActive) return;
    this.pointerActive = false;
    this.clearHold();

    if (this.didHold) {
      this.setPaused(false);
      this.didHold = false;
      return;
    }

    const target = ev.currentTarget as HTMLElement | null;
    const rect = target?.getBoundingClientRect();
    const frac = rect && rect.width > 0 ? (ev.clientX - rect.left) / rect.width : 0.5;
    if (frac > TAP_NEXT_MIN) this.next();
    else if (frac < TAP_PREV_MAX) this.prev();
    // middle third: no-op
  }

  onPointerCancel(): void {
    if (!this.pointerActive) return;
    this.pointerActive = false;
    this.clearHold();
    if (this.didHold) {
      this.setPaused(false);
      this.didHold = false;
    }
  }

  private setPaused(paused: boolean): void {
    this.paused = paused;
    const v = this.activeVideo();
    if (v && this.isVideoClip) {
      if (paused) {
        try {
          v.pause();
        } catch {
          /* ignore */
        }
      } else {
        v.play().catch(() => undefined);
      }
    }
    this.cdr.markForCheck();
  }

  // ---- keyboard (web parity: no hardware Back on desktop) ----

  @HostListener('keydown', ['$event'])
  onKeydown(ev: KeyboardEvent): void {
    switch (ev.key) {
      case 'Escape':
        ev.preventDefault();
        this.close();
        break;
      case 'ArrowRight':
        if (this.screen === 'reel') {
          ev.preventDefault();
          this.next();
        }
        break;
      case 'ArrowLeft':
        if (this.screen === 'reel') {
          ev.preventDefault();
          this.prev();
        }
        break;
    }
  }

  // ---- coach ----

  dismissCoach(): void {
    if (!this.showCoach) return;
    this.showCoach = false;
    this.markCoachShown();
    this.clearCoachTimer();
    this.cdr.markForCheck();
  }

  private scheduleCoachDismiss(): void {
    this.clearCoachTimer();
    this.ngZone.runOutsideAngular(() => {
      this.coachTimer = setTimeout(() => this.ngZone.run(() => this.dismissCoach()), COACH_AUTO_DISMISS_MS);
    });
  }

  private coachShownBefore(): boolean {
    try {
      return localStorage.getItem(COACH_KEY) === 'true';
    } catch {
      return false;
    }
  }

  private markCoachShown(): void {
    try {
      localStorage.setItem(COACH_KEY, 'true');
    } catch {
      /* ignore */
    }
  }

  // ---- catalog ----

  private currentClipTotalSeconds(): number {
    const c = this.currentClip;
    if (!c) return 0;
    if (this.isVideoClip) {
      const v = this.activeVideo();
      if (v && v.duration && isFinite(v.duration) && v.duration > 0) return v.duration;
    }
    return c.durationSeconds;
  }

  private loadCatalog(): void {
    const id = this.banner.endCardCatalogId;
    if (!id || this.catalogRequestedFor === id) return;
    this.catalogRequestedFor = id;
    this.catalogService.fetchCatalog(id).then(items => {
      this.catalog = items;
      this.cdr.markForCheck();
    });
  }

  // ---- timers ----

  private clearTick(): void {
    if (this.tickHandle) {
      clearInterval(this.tickHandle);
      this.tickHandle = null;
    }
  }
  private clearHold(): void {
    if (this.holdTimer) {
      clearTimeout(this.holdTimer);
      this.holdTimer = null;
    }
  }
  private clearRetry(): void {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }
  private clearCoachTimer(): void {
    if (this.coachTimer) {
      clearTimeout(this.coachTimer);
      this.coachTimer = null;
    }
  }
  private clearTimers(): void {
    this.clearHold();
    this.clearRetry();
    this.clearCoachTimer();
  }
}
